"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button, Input, Label, Checkbox, Text } from "@medusajs/ui";
import { subscribeUser } from "@lib/data/newsletter";
import ClientLink from "@modules/common/components/client-link";

// Email validation regex pattern
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email: string): boolean => {
  if (!email || email.length > 254) return false;
  return EMAIL_REGEX.test(email);
};
export default function Newsletter() {
  const t = useTranslations("newsletter");
  const [email, setEmail] = useState("");
  const [consents, setConsents] = useState({
    privacy_policy_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate email with secure regex
    if (!isValidEmail(email.trim())) {
      setError(t("error.Please enter a valid email address"));
      setLoading(false);
      return;
    }

    // Validate required consents
    if (!consents.privacy_policy_accepted // ||
    ) {
      setError(t("error.Please accept all required terms and conditions"));
      setLoading(false);
      return;
    }
    try {
      const response = await subscribeUser({
        email,
        ...consents
      });
      if (response.error) {
        const errorData = response.message;
        throw new Error(errorData || t("error.Failed to subscribe Please try again"));
      }
      setSuccess(true);
      setEmail("");
      setConsents({
        privacy_policy_accepted: false
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("error.Failed to subscribe Please try again"));
    } finally {
      setLoading(false);
    }
  };
  if (success) {
    return <div className="flex flex-col items-center justify-center py-20 px-4 sm:px-4">
        <div className="max-w-md text-center">
          <div className="mb-6">
            <svg className="w-16 h-16 mx-auto text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <Text className="txt-xlarge font-semibold mb-3">
            {t("success.Thank you for subscribing!")}
          </Text>
          <Text className="txt-medium text-ui-fg-subtle leading-relaxed">
            {t("success.You will soon receive exclusive content, updates, and resources directly in your inbox")}
          </Text>
        </div>
      </div>;
  }
  return <div className="pt-12 pb-12 small:pt-16 small:pb-16 px-8 sm:px-4" id="newsletter">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <Text className="text-4xl font-semibold mb-3 font-vangoura lowercase">
            {t("Subscribe to Our Newsletter")}
          </Text>
          <Text className="txt-medium text-ui-fg-subtle leading-relaxed">
            {t("Stay updated with the latest news, exclusive offers, and new releases")}
          </Text>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Input type="email" placeholder={t("Enter your email")} value={email} onChange={e_0 => setEmail(e_0.target.value)} disabled={loading} className="flex-1 sm:max-w-sm" />
            <Button type="submit" disabled={loading || !email} isLoading={loading} className="sm:w-auto w-full">
              {loading ? t("Subscribing") : t("Subscribe")}
            </Button>
          </div>

          {/* Consents */}
          <div className="flex justify-center">
            <div className="flex items-center gap-3">
              <Checkbox id="privacy_policy" checked={consents.privacy_policy_accepted} onCheckedChange={checked => setConsents(prev => ({
              ...prev,
              privacy_policy_accepted: checked === true
            }))} disabled={loading} />
              <Label htmlFor="privacy_policy" className="!text-xs text-ui-fg-subtle cursor-pointer hover:text-ui-fg-base transition-colors !transform-none">
                {t.rich("consent.I accept the Privacy Policy", {
                privacy: chunks => <ClientLink href="/privacy-policy" className="underline" target="_blank">
                      {chunks}
                    </ClientLink>
              })}
                *{/*{t("consent.I accept the Privacy Policy")} **/}
              </Label>
            </div>
          </div>

          {/* Error Message */}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>}

          {/* Required Fields Note */}
          {/*<Text className="text-xs text-ui-fg-muted text-center pt-2">
            * {t("consent.Required fields")}
           </Text>*/}
        </form>
      </div>
    </div>;
}